import React from "react";
import { Button } from "@/components/ui/button";
import type { BaseEntity } from "../../types";

export interface InfiniteScrollControlsProps<TData extends BaseEntity> {
  loadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  data: TData[];
}

/**
 * InfiniteScrollControls - Displays loading indicator and optional "Load More" button
 * 
 * The infinite scroll loading is handled automatically by useSimpleTableInfiniteScroll hook
 * when the user scrolls near the bottom. This component shows:
 * - Loading spinner when fetching next page
 * - "Load More" button as a manual fallback option
 * - "End of list" message when no more pages available
 */
export function InfiniteScrollControls<TData extends BaseEntity>({
  loadMore,
  hasNextPage,
  isFetchingNextPage,
  data,
}: InfiniteScrollControlsProps<TData>) {
  return (
    <>
      {/* Loading indicator - Shows when fetching next page */}
      {loadMore && isFetchingNextPage && (
        <div className="flex justify-center py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Loading more results...</span>
          </div>
        </div>
      )}

      {/* Load More button - Manual fallback option */}
      {loadMore && hasNextPage && !isFetchingNextPage && (
        <div className="flex justify-center py-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="outline"
            onClick={loadMore}
            disabled={isFetchingNextPage}
            className="gap-2"
          >
            Load More
          </Button>
        </div>
      )}

      {/* End of infinite scroll indicator */}
      {loadMore && !hasNextPage && data.length > 0 && (
        <div className="flex justify-center py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-500">You've reached the end of the list</div>
        </div>
      )}
    </>
  );
}
